import type { ConversationData } from './AgentInsightsContext';
import { decodeMessageContent } from './internal/conversation/decodeMessageContent';
import { formatPageContextForPrompt, PAGE_CONTEXT_MARKER } from './internal/pageContext';
import type { PageContext } from './internal/pageContext';
import { inferContextType, MENTION_MARKER_REGEX } from './resources';
import type { ResourceOrQuery, ResourceType } from './resources';

export { inferContextType, MENTION_MARKER_REGEX };
export type { ResourceType };

/**
 * Tool names that should be hidden from the conversation UI.
 * These are internal/loading tools that don't provide value to the user.
 */
const HIDDEN_TOOL_NAMES = new Set([
  'MCPSearch',
  'AskUserQuestion',
  'mcp__observability__launchdarkly_tag_flag',
  'mcp__observability__tag_conversation',
  'mcp__observability__untag_conversation',
  'mcp__observability__get_conversation_link',
]);

/**
 * Tool names that represent plan mode transitions.
 * These get special rendering instead of the default ShimmerText.
 */
export const PLAN_MODE_TOOL_NAMES = new Set(['EnterPlanMode', 'ExitPlanMode']);

/**
 * Returns true if the tool call is a plan mode tool (EnterPlanMode or ExitPlanMode).
 */
export const isPlanModeTool = (toolName: string): boolean => {
  return PLAN_MODE_TOOL_NAMES.has(toolName);
};

/**
 * Returns true if the tool call should be hidden from the user.
 */
export const isHiddenToolCall = (toolName: string): boolean => {
  return HIDDEN_TOOL_NAMES.has(toolName);
};

const STALE_CONVERSATION_MS = 90 * 60 * 1000; // 90 minutes

/**
 * True when the loaded conversation should show the "stopped early" warning.
 * Uses the conversation status from the backend as the source of truth:
 *   - "stopped" → show warning (user manually stopped)
 *   - "completed" / "active" with is_final → don't show
 *   - "active" without is_final and stale → show (agent likely crashed)
 */
export function isConversationIncomplete(data: ConversationData | null): boolean {
  if (!data?.messages?.length) {
    return false;
  }

  if (data.status === 'stopped') {
    return true;
  }

  // A failed conversation gets a dedicated error banner (see
  // transformConversationHistoryToComponents) — the "stopped early" warning
  // on top of it would be redundant.
  if (data.status === 'completed' || data.status === 'failed') {
    return false;
  }

  // Status is "active" — check if the conversation actually finished
  // (is_final message exists) or if it's stale (agent likely crashed).
  const hasFinalMessage = data.messages.some((m) => m.is_final);
  if (hasFinalMessage) {
    return false;
  }

  const last = data.messages[data.messages.length - 1];
  const lastMessageAge = Date.now() - new Date(last.created_at).getTime();
  return lastMessageAge > STALE_CONVERSATION_MS;
}

export const mapToolNameToUserFriendlyToolName = (toolName: string) => {
  switch (toolName) {
    case 'mcp__observability__logs':
      return 'Querying logs';
    case 'mcp__observability__metrics':
      return 'Querying metrics';
    case 'mcp__observability__traces':
      return 'Querying traces';
    case 'mcp__observability__sessions':
      return 'Querying sessions';
    case 'mcp__observability__error_groups':
      return 'Querying errors';
    case 'mcp__observability__timeline_events':
      return 'Querying session events';
    case 'mcp__observability__flag_evaluations':
      return 'Querying flag evaluations';
    case 'mcp__observability__github_repositories':
      return 'Examining available repositories';
    case 'mcp__observability__create_github_pull_request':
      return 'Creating a pull request';
    case 'mcp__observability__create_github_issue':
      return 'Creating a new issue';
    case 'mcp__observability__github_code_search':
      return 'Searching the codebase';
    case 'mcp__observability__search_github_pull_requests':
      return 'Searching existing pull requests';
    case 'mcp__observability__list_dashboards':
      return 'Listing dashboards';
    case 'mcp__observability__get_dashboard':
      return 'Loading dashboard';
    case 'mcp__observability__create_dashboard':
      return 'Creating dashboard';
    case 'mcp__observability__create_graph':
      return 'Adding chart to dashboard';
    case 'mcp__observability__preview_graph':
      return 'Previewing chart';
    case 'mcp__observability__list_alerts':
      return 'Listing alerts';
    case 'mcp__observability__preview_alert':
      return 'Previewing alert';
    case 'mcp__observability__create_alert':
      return 'Creating alert';
    case 'mcp__observability__get_keys':
      return 'Discovering available metrics';
    case 'mcp__observability__launchdarkly_environments':
      return 'Fetching LaunchDarkly environments';
    case 'mcp__observability__launchdarkly_flag_evaluations':
      return 'Fetching flag evaluations';
    case 'mcp__observability__launchdarkly_get_flag':
      return 'Fetching flag details';
    case 'mcp__observability__launchdarkly_get_flag_targeting':
      return 'Fetching flag targeting rules';
    case 'MCPSearch':
      return 'Loading MCP tool';
    case 'TodoWrite':
      return 'Updating todos';
    case 'EnterPlanMode':
      return 'Developing a plan';
    case 'ExitPlanMode':
      return 'Plan finalized';
    default:
      return toolName;
  }
};

export const buildPrompt = (userPrompt: string, resources: ResourceOrQuery[], pageContext?: PageContext) => {
  const hasResources = resources.length > 0;

  // Without resources travelling alongside it, a mention marker is noise — flatten it to its label.
  let prompt = hasResources ? userPrompt : userPrompt.replace(new RegExp(MENTION_MARKER_REGEX, 'g'), '$1');

  // Page context goes *before* the resource block on purpose:
  // `extractContextFromPrompt` slices from "Additional Context:" to the end of
  // the string, so anything appended after the resources gets swallowed into
  // the final resource's payload and shows up inside a context pill.
  if (pageContext) {
    prompt += formatPageContextForPrompt(pageContext);
  }

  if (!hasResources) {
    return prompt;
  }

  prompt += `\n\nAdditional Context: Here are some resources that may be relevant to the user question.`;

  resources.forEach((resource) => {
    if (!resource.payload) {
      return;
    }

    const type = inferContextType(resource);
    const formattedData = formatResourceForLLM(resource.payload, type);

    if (formattedData) {
      prompt += `\n--- ${type}:${resource.id} ---\n${formattedData}\n`;
    }
  });

  return prompt;
};

export const unBuildPrompt = (prompt: string) => {
  // Defensive: `message_preview` is typed `String!` in GraphQL, but malformed
  // or partial cache entries can hand us a non-string (e.g. an object), which
  // would throw on `.indexOf` below. Treat anything non-string as empty.
  if (typeof prompt !== 'string') {
    return '';
  }

  const additionalContextMarker = '\n\nAdditional Context:';
  const userQuestionPrefix = 'User Question: ';

  // Strip everything from the first machine-appended block onwards. Page
  // context is appended ahead of the resource block, so whichever marker comes
  // first is where the user's own text ends.
  const markerIndexes = [prompt.indexOf(additionalContextMarker), prompt.indexOf(PAGE_CONTEXT_MARKER)].filter(
    (index) => index !== -1,
  );
  const cutIndex = markerIndexes.length > 0 ? Math.min(...markerIndexes) : -1;
  let userSection = cutIndex !== -1 ? prompt.slice(0, cutIndex) : prompt;

  // Legacy prompts (pre-AI-Config era) embedded a system preamble before
  // "User Question: ...". Strip it so we only return what the user typed.
  const userQuestionIndex = userSection.indexOf(userQuestionPrefix);
  if (userQuestionIndex !== -1) {
    userSection = userSection.slice(userQuestionIndex + userQuestionPrefix.length);
  }

  return userSection.trim();
};

/**
 * Extract context resources from the prompt's "Additional Context" section.
 * Creates minimal ResourceOrQuery objects with just enough data for ContextPills to display.
 */
export const extractContextFromPrompt = (prompt: string): ResourceOrQuery[] => {
  const additionalContextMarker =
    'Additional Context: Here are some resources that may be relevant to the user question.';
  const contextIndex = prompt.indexOf(additionalContextMarker);

  if (contextIndex === -1) {
    return [];
  }

  const contextSection = prompt.slice(contextIndex + additionalContextMarker.length);
  const resources: ResourceOrQuery[] = [];

  const typePattern =
    'Sessions|Logs|Errors|Traces|Metrics|Dashboard|Graph|Alert|SavedSearch|ExternalSource|Flag|Unknown';
  // Match both new format "--- Type:id ---" and legacy format "--- Type ---".
  // Flag ids are `{projectKey}/{flagKey}` (and flag keys may include `/` or `.`),
  // so the id capture allows those characters in addition to word chars and `-`.
  const blockRegex = new RegExp(
    `---\\s*(${typePattern})(?::([\\w./-]+))?\\s*---\\n([\\s\\S]*?)(?=---\\s*(?:${typePattern})(?::[\\w./-]+)?\\s*---|$)`,
    'g',
  );

  let match;
  let legacyIndex = 0;
  while ((match = blockRegex.exec(contextSection)) !== null) {
    const type = match[1] as ResourceType;
    const id = match[2] || `historical-context-${legacyIndex++}`;
    const content = match[3].trim();

    const payload = createMinimalPayload(type, content);

    const validTypes = [
      'Sessions',
      'Logs',
      'Errors',
      'Traces',
      'Dashboard',
      'Graph',
      'Alert',
      'SavedSearch',
      'ExternalSource',
      'Flag',
    ] as const;
    const resourceType = validTypes.includes(type as (typeof validTypes)[number])
      ? (type as (typeof validTypes)[number])
      : undefined;

    resources.push({
      id,
      payload,
      type: resourceType,
    });
  }

  return resources;
};

/**
 * Create a minimal payload with just enough fields for ContextPills to display.
 * Only extracts what's needed for getLabel() and inferContextType().
 */
const createMinimalPayload = (type: ResourceType, content: string): Record<string, unknown> => {
  const getField = (fieldName: string): string | undefined => {
    const regex = new RegExp(`^${fieldName}:\\s*(.+)$`, 'm');
    const match = content.match(regex);
    return match?.[1]?.trim();
  };

  switch (type) {
    case 'Sessions':
      return {
        secure_id: getField('Session Secure ID') || 'Session',
        identifier: getField('Identifier'),
      };
    case 'Logs':
      return {
        message: getField('Message') || 'Log entry',
        level: getField('Level'),
      };
    case 'Errors':
      return {
        error_object_id: getField('Error ID') || 'error',
        event: [getField('Error Type') || getField('Error Message') || 'Error'],
      };
    case 'Traces':
      return {
        traceID: getField('Trace ID') || 'trace',
        spanName: getField('Operation'),
      };
    case 'Metrics':
      return {
        metrics: true,
      };
    case 'Dashboard':
      return {
        dashboard_id: getField('Dashboard ID') || 'dashboard',
        dashboard_name: getField('Dashboard Name'),
      };
    case 'Graph':
      return {
        graph_id: getField('Graph ID') || 'graph',
        graph_title: getField('Graph Title'),
        dashboard_id: getField('Dashboard ID'),
      };
    case 'Alert':
      return {
        alert_id: getField('Alert ID') || 'alert',
        name: getField('Alert Name'),
      };
    case 'SavedSearch':
      return {
        saved_search_id: getField('Saved Search ID') || 'saved-search',
        name: getField('Saved Search Name'),
        entity_type: getField('Entity Type'),
      };
    case 'ExternalSource':
      return {
        url: getField('URL') || '',
        sourceType: getField('Source Type') || 'unknown',
      };
    case 'Flag':
      return {
        flag_key: getField('Flag Key') || 'flag',
        project_key: getField('Project Key'),
        name: getField('Flag Name'),
        url: getField('URL'),
      };
    default:
      return { message: 'Context' };
  }
};

// Type for completed components used in AgentInsights
export type CompletedComponent = {
  type: 'text' | 'tool' | 'user' | 'error';
  content: unknown;
  toolName?: string;
  rawToolName?: string;
  context?: unknown;
  toolInputs?: unknown;
  // Set on `type: 'error'` components so the renderer can pick the right
  // banner copy + retry affordance per error code (e.g. INFERENCE_UNAVAILABLE
  // emitted by autofix when the Bedrock proxy circuit breaker trips).
  errorCode?: string;
  errorMessage?: string;
  // ISO timestamp of when the message was created. Set on `type: 'user'`
  // (when the prompt was submitted) and `type: 'text'` (when the assistant's
  // result landed) so the renderer can show it next to the copy affordance.
  timestamp?: string;
};

/**
 * Extract the human-readable markdown for a text/result component. Mirrors the
 * parsing in ResultMessage so copied text matches what is rendered: the agent
 * sometimes wraps its answer in a JSON `{ result: "..." }` envelope, but most
 * text components carry the markdown string directly. Returns '' when there is
 * nothing renderable.
 */
function isUnknownRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function extractResultText(content: unknown): string {
  try {
    const parsed = typeof content === 'string' ? JSON.parse(content) : content;
    if (isUnknownRecord(parsed) && typeof parsed.result === 'string') {
      return parsed.result;
    }
    return '';
  } catch {
    return typeof content === 'string' ? content : '';
  }
}

/**
 * Serialize a conversation's completed components into a readable markdown
 * transcript for copying. Only user prompts and Vega's text answers are
 * included — tool-call accordions and inline error banners are internal
 * scaffolding, not part of the response a user wants to paste elsewhere.
 */
export function serializeConversation(components: CompletedComponent[]): string {
  return components
    .map((component) => {
      if (component.type === 'user') {
        const text = typeof component.content === 'string' ? component.content.trim() : '';
        return text ? `**User:** ${text}` : '';
      }
      if (component.type === 'text') {
        const text = extractResultText(component.content).trim();
        return text ? `**Assistant:** ${text}` : '';
      }
      return '';
    })
    .filter(Boolean)
    .join('\n\n');
}

/**
 * Normalize a message `created_at` into an ISO-8601 string for the renderer,
 * which feeds it to `format`/`parseISO`. The history API may deliver
 * `created_at` as an ISO string, an epoch number, or a Date — `new Date`
 * accepts all three, whereas stringifying an epoch first (`String(1718…)`)
 * yields a value `parseISO` cannot read and `format` throws on. Returns
 * undefined for missing or unparseable values so the renderer omits the
 * timestamp instead of crashing on an `Invalid Date`.
 */
export function toIsoTimestamp(value: unknown): string | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const date = new Date(value as string | number | Date);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

export type ToolUseMap = Map<string, { name: string; input: unknown }>;

/**
 * Decompress and process a single conversation message into CompletedComponents.
 * The toolUseMap is mutated in place so that tool_result messages in later calls
 * can look up the tool_use that preceded them.
 *
 * Returns { components, wasTerminated, isResult } where components may be
 * empty (e.g. for system messages or empty final markers). isResult is true
 * when the decompressed payload is a "result" type message, which signals
 * conversation completion independently of the is_final flag.
 */
export function processConversationMessage(
  message: { role: string; content: any; is_final: boolean; id: string; created_at?: any },
  toolUseMap: ToolUseMap,
): { components: CompletedComponent[]; isResult: boolean } {
  const components: CompletedComponent[] = [];
  let isResult = false;

  if (message.role === 'system') {
    return { components, isResult };
  }

  if (message.role === 'user') {
    const prompt = message.content.content[0].text;
    const originalPrompt = unBuildPrompt(prompt);
    const context = extractContextFromPrompt(prompt);

    components.push({
      type: 'user',
      content: originalPrompt,
      context: context.length > 0 ? context : undefined,
      timestamp: toIsoTimestamp(message.created_at),
    });
    return { components, isResult };
  }

  if (message.role === 'assistant') {
    const response = message.content?.content?.[0]?.text;
    if (!response) {
      return { components, isResult };
    }

    const decompressedObject = decodeMessageContent(message.content);

    if (decompressedObject) {
      if (decompressedObject.type === 'result') {
        isResult = true;
      } else if (decompressedObject.type === 'vega_error') {
        // Typed terminal error envelope emitted by autofix's
        // claude_code_generator (e.g. when the Bedrock proxy circuit
        // breaker trips). Render as an inline banner; treat as a
        // result so polling stops.
        isResult = true;
        components.push({
          type: 'error',
          content: decompressedObject.message ?? '',
          errorCode: typeof decompressedObject.error_code === 'string' ? decompressedObject.error_code : undefined,
          errorMessage: typeof decompressedObject.message === 'string' ? decompressedObject.message : undefined,
        });
      } else if (decompressedObject.type === 'text' || decompressedObject.type === 'assistant') {
        decompressedObject.content.forEach((block: Record<string, unknown>) => {
          if (block.type === 'tool_use' && block.id) {
            toolUseMap.set(block.id as string, {
              name: (block.name as string) || 'Unknown Tool',
              input: block.input,
            });
          }
        });
        if (decompressedObject.content[0]?.text) {
          components.push({
            type: 'text',
            content: decompressedObject.content[0].text,
            timestamp: toIsoTimestamp(message.created_at),
          });
        }
      } else if (decompressedObject.type === 'user' && decompressedObject.content[0]?.type === 'tool_result') {
        const toolUseId = decompressedObject.content[0].tool_use_id as string;
        const toolUseInstance = toolUseMap.get(toolUseId);
        const rawToolName = toolUseInstance?.name ?? 'Unknown Tool';

        if (!isHiddenToolCall(rawToolName)) {
          const toolName = mapToolNameToUserFriendlyToolName(rawToolName);
          const content = decompressedObject.content[0].content?.[0]?.text ?? decompressedObject.content[0].content;
          components.push({
            type: 'tool',
            content,
            toolName,
            rawToolName,
            toolInputs: toolUseInstance?.input,
          });
        }
      }
    }
  }

  return { components, isResult };
}

/**
 * Split a conversation-level failure reason into code + message. The vega
 * runtime reports failed runs with a snake_case error class (budget_exceeded,
 * runtime_error, ...) and conversation_history formats the row's error as
 * "<class>: <message>". An error that doesn't match the format is shown
 * verbatim with the default banner copy.
 */
export function parseConversationError(error: string): { errorCode?: string; errorMessage: string } {
  const match = /^([a-z][a-z0-9_]*):\s+([\s\S]+)$/.exec(error);
  if (match) {
    return { errorCode: match[1], errorMessage: match[2] };
  }
  return { errorMessage: error };
}

/**
 * Does the most recent turn already carry an error banner? Scans back from the
 * end of the thread and stops at the latest user prompt, so an error from an
 * earlier turn (a `vega_error` envelope from a previous follow-up) can't
 * suppress the banner for the current failure. Shared by the history-load path
 * (transformConversationHistoryToComponents) and the live polling path in
 * AgentInsights so both dedupe on the same scope.
 */
export function currentTurnHasErrorComponent(components: CompletedComponent[]): boolean {
  for (let i = components.length - 1; i >= 0; i--) {
    if (components[i].type === 'error') {
      return true;
    }
    if (components[i].type === 'user') {
      return false;
    }
  }
  return false;
}

/**
 * Build the terminal error component for a failed conversation. Shared by the
 * history-load path (transformConversationHistoryToComponents) and the live
 * polling path in AgentInsights so both render the same banner.
 */
export function buildConversationErrorComponent(error: string | null | undefined): CompletedComponent {
  const parsed = error ? parseConversationError(error) : undefined;
  return {
    type: 'error',
    content: parsed?.errorMessage ?? '',
    errorCode: parsed?.errorCode,
    errorMessage: parsed?.errorMessage,
  };
}

export const transformConversationHistoryToComponents = (
  conversationData: ConversationData,
  toolUseMap: ToolUseMap,
): {
  conversationId: string;
  completedComponents: CompletedComponent[];
  isFinal: boolean;
  status: string;
} => {
  const completedComponents: CompletedComponent[] = [];
  let sawResult = false;

  for (const message of conversationData.messages) {
    try {
      const result = processConversationMessage(message, toolUseMap);
      completedComponents.push(...result.components);
      if (result.isResult) {
        sawResult = true;
      }
    } catch (error) {
      console.error('Error transforming conversation history to components:', error);
    }
  }

  const status = conversationData.status;
  const isStopped = status === 'stopped';
  const isCompleted = status === 'completed';
  const isFailed = status === 'failed';

  // A failed conversation is terminal: render an error banner instead of
  // leaving the thread looking in-progress (which would restart polling and
  // spin "Thinking..." forever). One banner per failed turn — skip when the
  // latest turn's messages already produced one (a typed vega_error envelope)
  // so we don't render two.
  if (isFailed && !currentTurnHasErrorComponent(completedComponents)) {
    completedComponents.push(buildConversationErrorComponent(conversationData.error));
  }

  const lastMsg = conversationData.messages[conversationData.messages.length - 1];
  const lastMsgIsFinal = lastMsg?.role === 'assistant' && lastMsg.is_final;

  return {
    conversationId: conversationData.conversationId,
    isFinal: isStopped || isCompleted || isFailed || sawResult || lastMsgIsFinal,
    status,
    completedComponents,
  };
};

const formatResourceForLLM = (payload: any, type: ResourceType): string => {
  if (!payload) {
    return '';
  }

  switch (type) {
    case 'Sessions':
      return formatSessionEntry(payload);
    case 'Logs':
      return formatLogEntry(payload);
    case 'Errors':
      return formatErrorEntry(payload);
    case 'Traces':
      return formatTraceEntry(payload);
    case 'Metrics':
      return formatMetricEntry(payload);
    case 'Dashboard':
      return formatDashboardEntry(payload);
    case 'Graph':
      return formatGraphEntry(payload);
    case 'Alert':
      return formatAlertEntry(payload);
    case 'SavedSearch':
      return formatSavedSearchEntry(payload);
    case 'ExternalSource':
      return formatExternalSourceEntry(payload);
    case 'Flag':
      return formatFlagEntry(payload);
    default:
      // Fallback to selective JSON for unknown types
      return formatGenericEntry(payload);
  }
};

/**
 * One-line summary of a graph's shape. Shared by the standalone `Graph` context
 * entry and the per-graph lines inside a `Dashboard` entry so the agent reads
 * the same vocabulary either way.
 */
const graphSummaryParts = (graph: any): string[] => {
  const parts: string[] = [];
  if (graph.title || graph.graph_title) {
    parts.push(`Title: ${graph.title || graph.graph_title}`);
  }
  if (graph.productType) {
    parts.push(`Data Source: ${graph.productType}`);
  }
  if (graph.type || graph.chart_type) {
    parts.push(`Chart Type: ${graph.type || graph.chart_type}`);
  }
  if (graph.query) {
    parts.push(`Query: ${graph.query}`);
  }
  if (graph.sql) {
    parts.push(`SQL: ${graph.sql}`);
  }
  if (Array.isArray(graph.expressions) && graph.expressions.length > 0) {
    const metrics = graph.expressions
      .map((e: { aggregator?: string; column?: string }) => `${e.aggregator ?? ''}(${e.column ?? ''})`)
      .join(', ');
    parts.push(`Metrics: ${metrics}`);
  }
  if (graph.groupByKeys) {
    const keys = Array.isArray(graph.groupByKeys) ? graph.groupByKeys.join(', ') : String(graph.groupByKeys);
    if (keys) {
      parts.push(`Group By: ${keys}`);
    }
  }
  if (Array.isArray(graph.funnelSteps) && graph.funnelSteps.length > 0) {
    const steps = graph.funnelSteps
      .map((s: { title?: string; query?: string }) => {
        const label = s.title || 'Untitled';
        return s.query ? `${label} (${s.query})` : label;
      })
      .join(' → ');
    parts.push(`Funnel Steps: ${steps}`);
  }
  if (graph.bucketInterval != null) {
    parts.push(`Bucket Interval: ${graph.bucketInterval}`);
  }
  if (graph.bucketCount != null) {
    parts.push(`Bucket Count: ${graph.bucketCount}`);
  }
  if (graph.limit != null) {
    parts.push(`Limit: ${graph.limit}`);
  }
  return parts;
};

/**
 * A single saved graph attached on its own (from a dashboard card, the graph
 * page, or an @-mention). Carries the surrounding dashboard plus the time range
 * and variables on screen, so "why did this spike?" resolves against the same
 * window the user is looking at.
 */
const formatGraphEntry = (payload: any): string => {
  const parts: string[] = [];

  if (payload.graph_id) {
    parts.push(`Graph ID: ${payload.graph_id}`);
  }
  if (payload.graph_title || payload.title) {
    parts.push(`Graph Title: ${payload.graph_title || payload.title}`);
  }
  if (payload.dashboard_id) {
    parts.push(`Dashboard ID: ${payload.dashboard_id}`);
  }
  if (payload.dashboard_name) {
    parts.push(`Dashboard Name: ${payload.dashboard_name}`);
  }

  // `graphSummaryParts` re-emits Title; drop it, we already wrote Graph Title.
  parts.push(...graphSummaryParts(payload).filter((part) => !part.startsWith('Title: ')));

  if (payload.bucketByKey) {
    parts.push(`Bucket By: ${payload.bucketByKey}`);
  }
  if (payload.start_date && payload.end_date) {
    parts.push(`Time Range: ${payload.start_date} to ${payload.end_date}`);
  }
  if (payload.variables && typeof payload.variables === 'object') {
    const rendered = Object.entries(payload.variables as Record<string, unknown>)
      .map(([key, value]) => `${key}=${Array.isArray(value) ? value.join('|') : String(value)}`)
      .join(', ');
    if (rendered) {
      parts.push(`Variables: ${rendered}`);
    }
  }

  return parts.join('\n');
};

const formatDashboardEntry = (payload: any): string => {
  const parts: string[] = [];

  if (payload.dashboard_id || payload.id) {
    parts.push(`Dashboard ID: ${payload.dashboard_id || payload.id}`);
  }
  if (payload.dashboard_name || payload.name) {
    parts.push(`Dashboard Name: ${payload.dashboard_name || payload.name}`);
  }

  if (Array.isArray(payload.graphs) && payload.graphs.length > 0) {
    parts.push(`Graph Count: ${payload.graphs.length}`);
    parts.push('Graphs:');
    for (const graph of payload.graphs) {
      parts.push(`  - ${graphSummaryParts(graph).join(' | ')}`);
    }
  }

  return parts.join('\n');
};

const formatAlertEntry = (payload: any): string => {
  const parts: string[] = [];

  if (payload.alert_id || payload.id) {
    parts.push(`Alert ID: ${payload.alert_id || payload.id}`);
  }
  if (payload.name) {
    parts.push(`Alert Name: ${payload.name}`);
  }
  if (payload.product_type) {
    parts.push(`Product Type: ${payload.product_type}`);
  }
  if (payload.disabled !== undefined) {
    parts.push(`Status: ${payload.disabled ? 'Disabled' : 'Active'}`);
  }
  if (payload.sql) {
    parts.push(`Query: ${payload.sql}`);
  }

  return parts.join('\n');
};

const formatSavedSearchEntry = (payload: any): string => {
  const parts: string[] = [];

  if (payload.saved_search_id || payload.id) {
    parts.push(`Saved Search ID: ${payload.saved_search_id || payload.id}`);
  }
  if (payload.name) {
    parts.push(`Saved Search Name: ${payload.name}`);
  }
  if (payload.entity_type) {
    parts.push(`Entity Type: ${payload.entity_type}`);
  }
  if (payload.params?.query) {
    parts.push(`Query: ${payload.params.query}`);
  }

  return parts.join('\n');
};

const formatSessionEntry = (payload: any): string => {
  const parts: string[] = [];

  if (payload.secure_id) {
    parts.push(`Session Secure ID: ${payload.secure_id}`);
  }
  if (payload.identifier) {
    parts.push(`Identifier: ${payload.identifier}`);
  }

  return parts.join('\n');
};

const formatLogEntry = (payload: any): string => {
  const parts: string[] = [];

  // Essential log information
  if (payload.timestamp) {
    parts.push(`Timestamp: ${payload.timestamp}`);
  }
  if (payload.serviceName) {
    parts.push(`Service: ${payload.serviceName}`);
  }
  if (payload.level || payload.severity) {
    parts.push(`Level: ${payload.level || payload.severity}`);
  }
  if (payload.message) {
    parts.push(`Message: ${payload.message}`);
  }

  // Key attributes (filter out noise)
  if (payload.logAttributes) {
    const relevantAttrs = filterRelevantAttributes(payload.logAttributes);
    if (Object.keys(relevantAttrs).length > 0) {
      parts.push(`Attributes: ${JSON.stringify(relevantAttrs, null, 2)}`);
    }
  }

  // Context information
  if (payload.traceId) {
    parts.push(`Trace ID: ${payload.traceId}`);
  }
  if (payload.spanId) {
    parts.push(`Span ID: ${payload.spanId}`);
  }

  return parts.join('\n');
};

const formatErrorEntry = (payload: any): string => {
  const parts: string[] = [];

  if (payload.timestamp) {
    parts.push(`Timestamp: ${payload.timestamp}`);
  }
  if (payload.serviceName) {
    parts.push(`Service: ${payload.serviceName}`);
  }
  if (payload.error_type || payload.errorType) {
    parts.push(`Error Type: ${payload.error_type || payload.errorType}`);
  }
  if (payload.error_message || payload.message) {
    parts.push(`Error Message: ${payload.error_message || payload.message}`);
  }
  if (payload.stack_trace || payload.stackTrace) {
    const stack = payload.stack_trace || payload.stackTrace;
    // Truncate very long stack traces
    const truncatedStack =
      typeof stack === 'string' && stack.length > 1000 ? `${stack.substring(0, 1000)}...[truncated]` : stack;
    parts.push(`Stack Trace:\n${truncatedStack}`);
  }

  if (payload.error_object_id) {
    parts.push(`Error ID: ${payload.error_object_id}`);
  }
  if (payload.traceId) {
    parts.push(`Trace ID: ${payload.traceId}`);
  }

  return parts.join('\n');
};

const formatTraceEntry = (payload: any): string => {
  const parts: string[] = [];

  if (payload.timestamp) {
    parts.push(`Timestamp: ${payload.timestamp}`);
  }
  if (payload.serviceName) {
    parts.push(`Service: ${payload.serviceName}`);
  }
  if (payload.operationName || payload.spanName) {
    parts.push(`Operation: ${payload.operationName || payload.spanName}`);
  }
  if (payload.duration) {
    parts.push(`Duration: ${payload.duration}ms`);
  }
  if (payload.status || payload.statusCode) {
    parts.push(`Status: ${payload.status || payload.statusCode}`);
  }

  if (payload.traceID || payload.trace_id) {
    parts.push(`Trace ID: ${payload.traceID || payload.trace_id}`);
  }
  if (payload.spanID || payload.span_id) {
    parts.push(`Span ID: ${payload.spanID || payload.span_id}`);
  }
  if (payload.parentSpanID || payload.parent_span_id) {
    parts.push(`Parent Span: ${payload.parentSpanID || payload.parent_span_id}`);
  }

  // Include relevant tags/attributes
  if (payload.tags || payload.attributes) {
    const attrs = payload.tags || payload.attributes;
    const relevantAttrs = filterRelevantAttributes(attrs);
    if (Object.keys(relevantAttrs).length > 0) {
      parts.push(`Attributes: ${JSON.stringify(relevantAttrs, null, 2)}`);
    }
  }

  return parts.join('\n');
};

const formatMetricEntry = (payload: any): string => {
  const parts: string[] = [];

  if (payload.timestamp) {
    parts.push(`Timestamp: ${payload.timestamp}`);
  }
  if (payload.metricName || payload.name) {
    parts.push(`Metric: ${payload.metricName || payload.name}`);
  }
  if (payload.value !== undefined) {
    parts.push(`Value: ${payload.value}`);
  }
  if (payload.unit) {
    parts.push(`Unit: ${payload.unit}`);
  }
  if (payload.serviceName) {
    parts.push(`Service: ${payload.serviceName}`);
  }

  // Include relevant dimensions/labels
  if (payload.dimensions || payload.labels || payload.tags) {
    const dims = payload.dimensions || payload.labels || payload.tags;
    const relevantDims = filterRelevantAttributes(dims);
    if (Object.keys(relevantDims).length > 0) {
      parts.push(`Dimensions: ${JSON.stringify(relevantDims, null, 2)}`);
    }
  }

  return parts.join('\n');
};

const formatExternalSourceEntry = (payload: any): string => {
  const parts: string[] = [];

  if (payload.sourceType) {
    parts.push(`Source Type: ${payload.sourceType}`);
  }
  if (payload.url) {
    parts.push(`URL: ${payload.url}`);
  }

  return parts.join('\n');
};

const formatFlagEntry = (payload: any): string => {
  const parts: string[] = [];
  const flagKey = payload.flag_key || payload.flagKey;
  const projectKey = payload.project_key || payload.projectKey;
  const name = payload.name || payload.title;

  if (flagKey) {
    parts.push(`Flag Key: ${flagKey}`);
  }
  if (projectKey) {
    parts.push(`Project Key: ${projectKey}`);
  }
  if (name) {
    parts.push(`Flag Name: ${name}`);
  }
  if (payload.url) {
    parts.push(`URL: ${payload.url}`);
  }

  return parts.join('\n');
};

const formatGenericEntry = (payload: any): string => {
  // For unknown types, show a curated selection of fields
  const relevantFields = filterRelevantAttributes(payload);
  return Object.entries(relevantFields)
    .map(([key, value]) => `${key}: ${typeof value === 'object' ? JSON.stringify(value) : value}`)
    .join('\n');
};

const filterRelevantAttributes = (attrs: any): Record<string, any> => {
  if (!attrs || typeof attrs !== 'object') {
    return {};
  }

  const filtered: Record<string, any> = {};
  const irrelevantKeys = new Set([
    // Common noise fields
    '__typename',
    '_id',
    'id',
    'created_at',
    'updated_at',
    // Overly verbose fields
    'raw_payload',
    'full_context',
    'metadata',
    // Internal fields
    '_internal',
    '__proto__',
    'constructor',
  ]);

  Object.entries(attrs).forEach(([key, value]) => {
    // Skip irrelevant keys
    if (irrelevantKeys.has(key)) {
      return;
    }

    // Skip very long strings (likely encoded data)
    if (typeof value === 'string' && value.length > 500) {
      return;
    }

    // Skip null/undefined values
    if (value === null || value === undefined) {
      return;
    }

    // Include the field
    filtered[key] = value;
  });

  return filtered;
};

/**
 * Composite key for a ResourceOrQuery that incorporates the resource type,
 * so that e.g. Alert "5" and Dashboard "5" are treated as distinct items.
 */
export const contextItemKey = (resource: ResourceOrQuery): string => `${inferContextType(resource)}:${resource.id}`;

/**
 * Merge context-pill resources with editor mention resources, deduping by
 * `contextItemKey` (type + id, so Alert "5" and Dashboard "5" stay distinct).
 * Context items come first so they win for ordering when a resource appears in
 * both surfaces. See the "mentions vs. context pills" Slack thread — pills and
 * mentions are independent surfaces that can legitimately reference the same
 * resource, but the outgoing prompt should only include it once.
 */
export const mergeAndDedupeResources = (
  contextResources: ResourceOrQuery[],
  mentionResources: ResourceOrQuery[],
): ResourceOrQuery[] => {
  const seen = new Set<string>();
  return [...contextResources, ...mentionResources].filter((r) => {
    const key = contextItemKey(r);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};
